import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { ArrowDropDown } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import { Box } from '@mui/system'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import {
    useCalculateDeliveryPriceMutation,
    useCalculateTaxRateMutation,
    useUpdateQuoteMutation,
} from '@/app/services/quotes'
import { selectSelectedCustomer } from '@/app/slices/customersSlice'
import { selectOrganisation, selectOrganisationId, selectPaidFeatures } from '@/app/slices/organisationSlice'
import { setSelectedQuoteStatus } from '@/app/slices/quoteItemsSlice'
import { TbxTooltip } from '@/common/components'
import TbxLocalizationProvider from '@/common/components/TbxLocalizationProvider/TbxLocalizationProvider'
import { useToolBoxTreatments } from '@/common/hooks'
import {
    DELIVERY_PRICING_METHODS,
    QUOTE_PAYMENT_STATUS,
    QUOTE_SOURCE_TYPES,
    QUOTE_STATUS_ARRAY,
    SHIPPING_OPTIONS,
} from '@/common/utils/Constants/Constants'

import QuoteShippingBreakdown from './QuoteShippingBreakdown'

const classes = {
    shippingInputs: {
        width: 'calc(100% / 5)',
    },

    disabledInput: {
        '& .Mui-disabled': {
            textFillColor: '#34495E',
        },
        '& .MuiInput-input.Mui-disabled': {
            fontWeight: 'bold',
        },
    },
    divider: {
        borderStyle: 'dashed',
        borderColor: blueGrey[200],
    },

    disabledMenuItem: {
        '& .MuiMenuItem-root.Mui-disabled': {
            pointerEvents: 'initial',
        },
    },
}

const QuoteShippingOptions = ({ quote }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const { enqueueSnackbar } = useSnackbar()

    const { showShipping } = useToolBoxTreatments()

    const organisation = useSelector(selectOrganisation)
    const organisationId = useSelector(selectOrganisationId)
    const { hasShipping } = useSelector(selectPaidFeatures)
    const selectedCustomer = useSelector(selectSelectedCustomer)

    const [updateQuote] = useUpdateQuoteMutation()
    const [calculateDeliveryPrice, { isLoading: isCalculatingDelivery }] = useCalculateDeliveryPriceMutation({
        fixedCacheKey: 'shared-calculate-delivery-price',
    })
    const [calculateTaxRate, { isLoading: isCalculatingTax }] = useCalculateTaxRateMutation({
        fixedCacheKey: 'shared-calculate-tax-rate',
    })

    const [shippingOption, setShippingOption] = useState(quote?.deliveryOption ?? null)

    // Follow the quote's delivery option when it changes underneath us (another
    // component or a refetch), adjusting during render instead of in an effect.
    // See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    const [prevDeliveryOption, setPrevDeliveryOption] = useState(quote?.deliveryOption)
    if (quote?.deliveryOption !== prevDeliveryOption) {
        setPrevDeliveryOption(quote?.deliveryOption)
        setShippingOption(quote?.deliveryOption ?? null)
    }
    const [requireShippingDate, setRequireShippingDate] = useState(quote?.requiredDateUtc ?? null)
    const [deliveryPricingMethod, setDeliveryPricingMethod] = useState(quote?.deliveryPricingMethod ?? null)
    const [customerDeliveryProvider, setCustomerDeliveryProvider] = useState(
        quote?.customerDeliveryProvider ?? quote?.deliveryProvider
    )
    const [customerDeliveryAccountNumber, setCustomerDeliveryAccountNumber] = useState(
        quote?.customerDeliveryAccountNumber ?? null
    )

    const userTimeZone = dayjs.tz.guess()

    const showShippingBreakdown =
        shippingOption === 'Delivery' && deliveryPricingMethod === DELIVERY_PRICING_METHODS.ChargeToOrder.value

    const isIssued = [QUOTE_STATUS_ARRAY.Issued].includes(quote?.status)

    const isOrdered = [QUOTE_STATUS_ARRAY.Ordered].includes(quote?.status)

    const isInvoiced = [QUOTE_STATUS_ARRAY.Invoiced].includes(quote?.status)

    const isPending = [QUOTE_STATUS_ARRAY.PendingOrderConfirmation].includes(quote?.status)

    const isPaid = isOrdered && quote?.paymentStatus !== QUOTE_PAYMENT_STATUS.Unpaid

    const isDisabled = [
        QUOTE_STATUS_ARRAY.Lost,
        QUOTE_STATUS_ARRAY.Cancelled,
        QUOTE_STATUS_ARRAY.Rejected,
        QUOTE_STATUS_ARRAY.Voided,
    ].includes(quote?.status)

    const isReadOnly = isInvoiced || isPending || isPaid

    const hasCustomerAssigned = Boolean(quote?.customerContactId && quote?.customerName)
    const customerHasAddress = hasCustomerAssigned && Boolean(selectedCustomer?.deliveryAddress)

    const DELIVERY_PRICING_METHODS_OPTIONS = Object.values(DELIVERY_PRICING_METHODS).filter((method) =>
        method.sources.includes(QUOTE_SOURCE_TYPES[quote?.quoteSourceType])
    )

    const handleCalculateQuote = async () => {
        try {
            await calculateDeliveryPrice({ organisationId, quoteId: quote?.id }).unwrap()
            await calculateTaxRate({ organisationId, quoteId: quote?.id }).unwrap()
        } catch (error) {
            const errorMessage = t(error?.data)
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const updateQuoteHandler = async (attribute, value) => {
        if (value === '' && !['customerDeliveryAccountNumber', 'customerDeliveryProvider'].includes(attribute)) return
        let changeAddress = false
        let customerBillingAddress = null
        let customerDeliveryAddress = null

        if (attribute === 'deliveryOption' && value === 'Delivery') {
            changeAddress = true
            customerBillingAddress = selectedCustomer?.billingAddress || null
            customerDeliveryAddress = selectedCustomer?.deliveryAddress || null
        }

        try {
            await updateQuote({
                organisationId,
                quoteId: quote?.id,
                quote: {
                    ...quote,
                    ...(['deliveryPricingMethod', 'deliveryOption'].includes(attribute) &&
                        quote.status !== QUOTE_STATUS_ARRAY.NotCalculated &&
                        !isOrdered &&
                        !isIssued && { status: QUOTE_STATUS_ARRAY.NotCalculated }),
                    [attribute]: value,
                    ...(changeAddress && {
                        deliveryAddressId: customerDeliveryAddress?.addressId ?? null,
                        deliveryAddress: customerDeliveryAddress,
                        billingAddressId: customerBillingAddress?.addressId ?? null,
                        billingAddress: customerBillingAddress,
                    }),
                },
            }).unwrap()

            if (['deliveryPricingMethod', 'deliveryOption'].includes(attribute)) {
                if ((isIssued || isOrdered) && !isPaid) {
                    handleCalculateQuote()
                }
            }
        } catch (error) {
            enqueueSnackbar(t('Failed to update the quote'), {
                variant: 'error',
            })
        }
    }

    const debouncedUpdateQuoteHandler = useDebouncedCallback(updateQuoteHandler, 1000)

    const handleShippingOptionChange = (event) => {
        setShippingOption(event.target.value)
        updateQuoteHandler('deliveryOption', event.target.value)
    }

    const handleDeliveryPricingMethodChange = (event) => {
        if (event.target.value === 'ChargeToOrder' && !hasShipping) return

        if (quote.status !== QUOTE_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedQuoteStatus(QUOTE_STATUS_ARRAY.NotCalculated))
        }

        setDeliveryPricingMethod(event.target.value)
        updateQuoteHandler('deliveryPricingMethod', event.target.value)
    }

    const handleRequireShippingDateChange = (date) => {
        setRequireShippingDate(date)
        updateQuoteHandler('requiredDateUtc', date?.utc().format() || null)
    }

    const handleDeliveryProviderChange = (event) => {
        setCustomerDeliveryProvider(event.target.value)
        debouncedUpdateQuoteHandler('customerDeliveryProvider', event.target.value)
    }

    const handleDeliveryProviderBlur = (event) => {
        if (event.target.value === '') {
            setCustomerDeliveryProvider(
                quote?.customerDeliveryProvider || organisation?.internalPreferredDeliveryProvider
            )
        }
    }

    const handleDeliveryAccountNumberChange = (event) => {
        setCustomerDeliveryAccountNumber(event.target.value)
        debouncedUpdateQuoteHandler('customerDeliveryAccountNumber', event.target.value)
    }

    return (
        <TbxLocalizationProvider>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 3,
                }}
            >
                <Typography variant="strong2">{t('Shipping and delivery options')}</Typography>

                {showShippingBreakdown ? <QuoteShippingBreakdown quote={quote} /> : null}
            </Box>
            <Box
                sx={{
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 3,
                    justifyContent: 'flex-start',
                }}
            >
                <TbxTooltip
                    title={
                        (isReadOnly &&
                            t(
                                'Shipping option cannot be changed after the quote is paid or invoiced or if is pending order confirmation'
                            )) ||
                        (!hasCustomerAssigned && t('A customer must be assigned before delivery can be selected.')) ||
                        (!customerHasAddress &&
                            t('The customer needs a delivery address before delivery can be selected.')) ||
                        ''
                    }
                    arrow
                >
                    <FormControl
                        disabled={
                            isDisabled ||
                            isReadOnly ||
                            !hasCustomerAssigned ||
                            !customerHasAddress ||
                            isCalculatingTax ||
                            isCalculatingDelivery
                        }
                        sx={[classes.shippingInputs, classes.disabledInput]}
                    >
                        <InputLabel
                            color="secondary"
                            id="select-shipping-option-label"
                            shrink={true}
                        >
                            {t('Shipping option')}
                        </InputLabel>
                        <Select
                            color="secondary"
                            disableUnderline={isDisabled || isReadOnly}
                            IconComponent={() => (isDisabled || isReadOnly ? null : <ArrowDropDown color="action" />)}
                            id="select-shipping-option"
                            inputProps={{
                                'data-testid': 'quote-shipping-option-input',
                            }}
                            label={t('Shipping option')}
                            labelId="select-shipping-option-label"
                            value={shippingOption ?? ''}
                            variant="standard"
                            onChange={handleShippingOptionChange}
                        >
                            {SHIPPING_OPTIONS.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {t(option.label)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </TbxTooltip>
                {showShipping && shippingOption === 'Delivery' ? (
                    <TbxTooltip
                        title={
                            isReadOnly
                                ? t(
                                      'Delivery pricing method cannot be changed after the quote is paid or invoiced or if is pending order confirmation'
                                  )
                                : ''
                        }
                        arrow
                    >
                        <FormControl
                            disabled={isDisabled || isReadOnly}
                            sx={[classes.shippingInputs, classes.disabledInput]}
                            fullWidth
                        >
                            <InputLabel
                                color="secondary"
                                id="select-delivery-pricing-method-label"
                                shrink={true}
                            >
                                {t('Delivery pricing method')}
                            </InputLabel>
                            <Select
                                color="secondary"
                                disableUnderline={isDisabled || isReadOnly}
                                IconComponent={() =>
                                    isDisabled || isReadOnly ? null : <ArrowDropDown color="action" />
                                }
                                id="select-delivery-pricing-method"
                                inputProps={{
                                    'data-testid': 'quote-delivery-pricing-method-input',
                                }}
                                label={t('Delivery pricing method')}
                                labelId="select-delivery-pricing-method-label"
                                MenuProps={{
                                    sx: classes.disabledMenuItem,
                                }}
                                value={deliveryPricingMethod ?? ''}
                                variant="standard"
                                onChange={handleDeliveryPricingMethodChange}
                            >
                                {DELIVERY_PRICING_METHODS_OPTIONS.map((option) => (
                                    <MenuItem
                                        disabled={option.value === 'ChargeToOrder' ? !hasShipping : null}
                                        key={option.value}
                                        value={option.value}
                                    >
                                        <TbxTooltip
                                            key={option.value}
                                            title={
                                                option.value === 'ChargeToOrder' && !hasShipping
                                                    ? t('This feature is not included in the current plan.')
                                                    : ''
                                            }
                                        >
                                            <span>{t(option.shortLabel)}</span>
                                        </TbxTooltip>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </TbxTooltip>
                ) : null}
                {showShipping && shippingOption === 'Delivery' && deliveryPricingMethod === 'PayUponDelivery' ? (
                    <>
                        <TextField
                            color="secondary"
                            disabled={isDisabled}
                            label={t('Delivery provider')}
                            placeholder={t('To be confirmed')}
                            slotProps={{
                                input: {
                                    disableUnderline: isDisabled,
                                },

                                htmlInput: {
                                    'data-testid': 'quote-customer-delivery-provider-input',
                                },

                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            sx={[classes.disabledInput, classes.shippingInputs]}
                            value={customerDeliveryProvider}
                            onBlur={handleDeliveryProviderBlur}
                            onChange={handleDeliveryProviderChange}
                        />

                        <TextField
                            color="secondary"
                            disabled={isDisabled}
                            label={t('Delivery account number')}
                            placeholder={t('N/A')}
                            slotProps={{
                                input: {
                                    disableUnderline: isDisabled,
                                },

                                htmlInput: {
                                    'data-testid': 'quote-customer-delivery-account-number-input',
                                },

                                inputLabel: {
                                    shrink: true,
                                },
                            }}
                            sx={[classes.disabledInput, classes.shippingInputs]}
                            value={customerDeliveryAccountNumber}
                            onChange={handleDeliveryAccountNumberChange}
                        />
                    </>
                ) : null}

                <DatePicker
                    closeOnSelect={false}
                    disabled={isDisabled}
                    disableOpenPicker={isDisabled}
                    format="DD-MMM-YYYY"
                    label={t('Required shipping date')}
                    slotProps={{
                        textField: {
                            sx: classes.disabledInput,
                            color: 'secondary',
                            placeholder: t('Select a date'),
                            variant: 'standard',

                            inputProps: {
                                'data-testid': 'quote-require-shipping-date-input',
                            },
                            InputProps: {
                                disableUnderline: isDisabled,
                            },
                            InputLabelProps: {
                                shrink: true,
                            },
                        },
                        field: {
                            readOnly: true,
                        },
                        actionBar: {
                            actions: ['clear', 'cancel', 'accept'],
                            disableSpacing: true,
                        },
                    }}
                    timezone={userTimeZone}
                    value={requireShippingDate ? dayjs(requireShippingDate) : null}
                    disablePast
                    onAccept={handleRequireShippingDateChange}
                />
            </Box>
        </TbxLocalizationProvider>
    )
}

QuoteShippingOptions.propTypes = {
    quote: PropTypes.object,
}

export default QuoteShippingOptions
